import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, Grid, Button, Chip, Divider } from '@mui/material';
import gsap from 'gsap';

/**
 * @component MarketDataVisualization
 * @description Market data visualization component for the minerals trading platform
 *
 * SEO Considerations:
 * - Displays real-time market data for key minerals (copper, cobalt, lithium, gold, silver)
 * - Enhances user understanding of mineral pricing and market trends
 * - Uses semantic HTML structure with proper heading hierarchy
 * - Provides text alternatives for visual data representations
 * - Includes keyword-rich content for mineral market data
 */
const MarketDataVisualization = () => {
  // State for market data
  const [marketData, setMarketData] = useState({
    gold: { price: 1923.45, change: 2.3 },
    silver: { price: 23.78, change: -0.5 },
    copper: { price: 4.12, change: 1.7 },
    lithium: { price: 76.34, change: 5.2 },
    cobalt: { price: 32.56, change: -1.8 }
  });

  // References for animation elements
  const chartRef = useRef(null);
  const barRefs = useRef([]);
  const priceRefs = useRef([]);

  /**
   * Simulate market data updates for ethical minerals trading visualization
   * - Updates pricing data for key minerals (copper, cobalt, lithium, gold, silver)
   * - Provides dynamic content that engages users while maintaining SEO value
   * - Demonstrates real-time market monitoring capabilities of the platform
   */
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketData(prevData => {
        const newData = { ...prevData };

        // Update each mineral with random price changes
        Object.keys(newData).forEach(mineral => {
          const randomChange = (Math.random() * 2 - 1) * 0.5; // Random change between -0.5% and 0.5%
          const currentPrice = newData[mineral].price;
          const newPrice = parseFloat((currentPrice * (1 + randomChange / 100)).toFixed(2));
          const newChangePercent = parseFloat((newData[mineral].change + randomChange).toFixed(1));

          newData[mineral] = {
            price: newPrice,
            change: newChangePercent
          };
        });

        return newData;
      });
    }, 3000); // Update every 3 seconds

    return () => clearInterval(interval);
  }, []);

  /**
   * Animate market data visualization bars for ethical minerals
   * - Provides visual representation of price changes for key minerals
   * - Uses color coding (green/red) to indicate price movement
   * - Enhances user experience while maintaining SEO-friendly content structure
   */
  useEffect(() => {
    if (barRefs.current.length > 0) {
      Object.keys(marketData).forEach((mineral, index) => {
        const barHeight = Math.abs(marketData[mineral].change) * 5; // Scale for better visualization
        const isPositive = marketData[mineral].change >= 0;

        gsap.to(barRefs.current[index], {
          height: `${barHeight}px`,
          backgroundColor: isPositive ? '#4caf50' : '#f44336',
          duration: 0.5,
          ease: 'power2.out'
        });

        gsap.to(priceRefs.current[index], {
          y: isPositive ? -5 : 5,
          duration: 0.3,
          ease: 'power2.out',
          yoyo: true,
          repeat: 1
        });
      });
    }
  }, [marketData]);

  // Generate random trade data
  const generateRandomTrades = () => {
    const minerals = ['Gold', 'Silver', 'Copper', 'Lithium', 'Cobalt', 'Tin', 'Zinc'];
    const countries = ['DRC', 'South Africa', 'Australia', 'Chile', 'Peru', 'Canada', 'Russia'];
    const companies = ['MineralCorp', 'GeoResources', 'EarthMetals', 'PreciousOres', 'MetalWorks'];

    return Array.from({ length: 5 }, (_, i) => {
      const mineral = minerals[Math.floor(Math.random() * minerals.length)];
      const origin = countries[Math.floor(Math.random() * countries.length)];
      const destination = countries[Math.floor(Math.random() * countries.length)];
      const amount = Math.floor(Math.random() * 1000) + 100;
      const company = companies[Math.floor(Math.random() * companies.length)];
      const status = Math.random() > 0.7 ? 'Pending' : 'Completed';

      return {
        id: i + 1,
        mineral,
        origin,
        destination,
        amount,
        company,
        status
      };
    });
  };

  const [recentTrades, setRecentTrades] = useState(generateRandomTrades());

  // Update recent trades periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setRecentTrades(generateRandomTrades());
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <Paper
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 3,
        border: '2px solid #f5e5a5',
        borderLeft: '6px solid #eab308',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        background: 'linear-gradient(to right, #fffef0, #fffde7)',
        p: 3,
        mb: 4,
        overflow: 'hidden'
      }}
    >
      <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        Live Market Data & Trading Activity
      </Typography>

      <Grid container spacing={3}>
        {/* Market Prices Section */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              bgcolor: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Mineral Prices (USD)
            </Typography>

            <Grid container spacing={2}>
              {Object.entries(marketData).map(([mineral, data], index) => (
                <Grid item xs={6} sm={4} key={mineral}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid #f0f0f0',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                      {mineral}
                    </Typography>
                    <Typography
                      ref={el => priceRefs.current[index] = el}
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: data.change >= 0 ? '#4caf50' : '#f44336'
                      }}
                    >
                      ${data.price}
                    </Typography>
                    <Chip
                      label={`${data.change >= 0 ? '+' : ''}${data.change}%`}
                      size="small"
                      color={data.change >= 0 ? 'success' : 'error'}
                      sx={{ mt: 1, minWidth: 60 }}
                    />
                    <Box
                      sx={{
                        mt: 1,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-end',
                        height: '30px'
                      }}
                    >
                      <Box
                        ref={el => barRefs.current[index] = el}
                        sx={{
                          width: '8px',
                          height: `${Math.abs(data.change) * 5}px`,
                          bgcolor: data.change >= 0 ? '#4caf50' : '#f44336',
                          borderRadius: '4px 4px 0 0'
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Recent Trades Section */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Recent Transactions
            </Typography>

            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              {recentTrades.map((trade, index) => (
                <React.Fragment key={trade.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      px: 1,
                      borderRadius: 1,
                      bgcolor: index % 2 === 0 ? 'rgba(234, 179, 8, 0.05)' : 'transparent'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {trade.mineral} ({trade.amount}kg)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {trade.origin} → {trade.destination} | {trade.company}
                      </Typography>
                    </Box>
                    <Chip
                      label={trade.status}
                      size="small"
                      color={trade.status === 'Completed' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </Box>
                  {index < recentTrades.length - 1 && (
                    <Divider sx={{ my: 0.5 }} />
                  )}
                </React.Fragment>
              ))}
            </Box>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                sx={{ borderRadius: 50, px: 3 }}
              >
                View All Transactions
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Platform Statistics */}
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              bgcolor: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Platform Statistics
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#eab308' }}>
                    1,250+
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Registered Users
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#eab308' }}>
                    $42M+
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Transaction Volume
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#eab308' }}>
                    35+
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Countries
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={6} sm={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#eab308' }}>
                    12
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mineral Types
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default MarketDataVisualization;
